import {
  ICheckboxStyleProps,
  ICheckboxStyles,
} from "@fluentui/react/lib/Checkbox";
import { IStyle, ITheme, mergeStyleSets } from "@fluentui/react/lib/Styling";
import {
  IStyleFunctionOrObject,
  memoizeFunction,
} from "@fluentui/react/lib/Utilities";

import {
  baseFabricFocusVisibleSelector,
  commonHighContrastDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastOutlineToggledOn,
  controlStylingConstants,
  getInnerBorderStyles,
  getStandardControlSemanticColorsWithTheme,
  StandardControlSemanticColors,
  textContainerClassName,
} from "@1js/acui-common";

const checkboxClassName = "checkbox-checkbox";
const checkboxTextClassName = "checkbox-text";
const checkboxCheckClassName = "checkbox-check";

export const getCheckBoxStylesInMenu = memoizeFunction(
  (): Partial<ICheckboxStyles> => {
    return {
      root: {
        width: "100%",
      },
    };
  }
);

export const getCheckBoxStylesInMLRMenu = memoizeFunction(
  (): Partial<ICheckboxStyles> => {
    return {
      root: {
        width: "100%",
        height: 28,
        lineHeight: 28,
      },
    };
  }
);

const checkboxSize = 20;

const checkboxHoverStyles: IStyle = [
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
];

const checkHoverStyles = (
  standardControlSemanticColors: StandardControlSemanticColors
): IStyle => {
  return [
    {
      opacity: 1,
      fill: standardControlSemanticColors.checkBoxHoverCheckmarkColor,
    },
    checkboxCheckHighContrastStyling,
  ];
};

const checkboxCheckHighContrastStyling = {
  selectors: {
    "svg > path.OfficeIconColors_HighContrast": {
      fill: "HighlightText",
    },
  },
};

const checkBoxFocusStyles: IStyle = [
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
];

const buttonHoverFocusStyles = [
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
];

const getCheckboxClassNames = memoizeFunction(
  (
    theme: ITheme,
    focused: boolean,
    checked: boolean,
    disabled: boolean
  ): ICheckboxStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return mergeStyleSets({
      root: [
        {
          border: `1px solid transparent`,
          background: "none",
          cursor: "default",
          display: "flex",
          height: controlStylingConstants.controlHeight,
          boxSizing: "border-box",
          margin: 0,
          outline: "none",
          padding: "0 9px",
          position: "relative",
        },
        !disabled && [
          {
            selectors: {
              [`:hover .${checkboxCheckClassName}`]: checkHoverStyles(
                standardControlSemanticColors
              ),
              [`:hover .${checkboxClassName}`]: checkboxHoverStyles,
              [`:hover .${checkboxTextClassName}`]:
                commonHighContrastHoverFocusStyles,
              ":hover": buttonHoverFocusStyles,
              [`:active .${checkboxClassName}`]: [
                {
                  background:
                    standardControlSemanticColors.checkBoxPressedBackgroundColor,
                },
                checkboxHoverStyles,
              ],
              [`:active .${checkboxCheckClassName}`]: [
                {
                  fill: standardControlSemanticColors.checkMarkColor,
                },
                checkboxCheckHighContrastStyling,
              ],
            },
          },
        ],
        disabled && [commonHighContrastDisabledStyles],
        checked &&
          !disabled && {
            selectors: {
              [`:hover .${checkboxCheckClassName}`]: [
                {
                  fill: standardControlSemanticColors.checkMarkColor,
                },
                checkboxCheckHighContrastStyling,
              ],
              [`:hover .${checkboxClassName}`]: [
                {
                  background:
                    standardControlSemanticColors.checkBoxHoverBackgroundColor,
                },
                checkboxHoverStyles,
              ],
            },
          },
        focused && [
          {
            selectors: {
              [`${baseFabricFocusVisibleSelector} &`]: {
                borderColor: standardControlSemanticColors.focusedBorderColor,
                ":after": getInnerBorderStyles({
                  width: 2,
                  color: standardControlSemanticColors.focusedBorderColor,
                  gap: -1,
                }),
              },
            },
          },
          buttonHoverFocusStyles,
        ],
      ],
      input: [
        {
          position: "absolute",
          opacity: 0,
          background: "none",
        },
      ],
      checkbox: [
        checkboxClassName,
        {
          borderColor: standardControlSemanticColors.checkBoxBorderColor,
          height: checkboxSize,
          width: checkboxSize,
          display: "flex",
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          borderWidth: 1,
          borderStyle: "solid",
          margin: "0 4px",
          boxSizing: "border-box",
          transitionProperty: "background, border, border-color",
          transitionDuration: "200ms",
          transitionTimingFunction: "cubic-bezier(.4, 0, .23, 1)",
          overflow: "hidden",
          backgroundColor: standardControlSemanticColors.inputBackgroundColor,
        },
        !disabled && [
          {
            selectors: {
              ":hover": checkboxHoverStyles,
              ":focus": checkBoxFocusStyles,
            },
          },
        ],
        checked && [
          commonHighContrastHoverFocusStyles,
          commonHighContrastOutlineToggledOn,
        ],
        disabled && [
          {
            backgroundColor: "none",
            selectors: {
              ":focus": commonHighContrastOutlineFocusedDisabled,
            },
          },
          commonHighContrastDisabledStyles,
        ],
        disabled &&
          checked && [
            {
              background:
                standardControlSemanticColors.checkMarkCheckDisableBackgroundColor,
              selectors: {
                ":focus": commonHighContrastOutlineFocusedDisabled,
              },
            },
            commonHighContrastDisabledStyles,
          ],
        focused && checkBoxFocusStyles,
      ],
      checkmark: [
        checkboxCheckClassName,
        {
          opacity: 0,
          display: "inline-flex",
        },
        checkboxCheckHighContrastStyling,
        checked && [
          {
            opacity: 1,
            width: 20,
            height: 20,
            fill: standardControlSemanticColors.checkMarkSelectedColor,
          },
          checkboxCheckHighContrastStyling,
        ],
        checked &&
          disabled && [
            {
              fill: standardControlSemanticColors.checkMarkDisabledColor,
              opacity: 1,
            },
            checkboxCheckHighContrastStyling,
          ],
        focused && checkboxCheckHighContrastStyling,
      ],
      text: [
        checkboxTextClassName,
        textContainerClassName,
        {
          color: standardControlSemanticColors.inputTextColor,
          marginLeft: 5,
          marginRight: 4,
          fontSize: 14,
        },
        disabled && [
          {
            color: standardControlSemanticColors.inputTextDisabledColor,
          },
          commonHighContrastDisabledStyles,
        ],
        focused && [
          {
            color: standardControlSemanticColors.checkBoxFocusedTextColor,
          },
          commonHighContrastHoverFocusStyles,
        ],
      ],
      label: [
        {
          alignItems: "center",
          cursor: "default",
          display: "flex",
          margin: "0 -4px",
          position: "relative",
          textAlign: "left",
          userSelect: "none",
        },
        disabled && { cursor: "default" },
      ],
    });
  }
);

export const getStyles = (props: ICheckboxStyleProps): ICheckboxStyles => {
  return {
    label: [
      props.reversed && {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
      },
    ],
  };
};

export const getCheckboxStyles: (
  theme: ITheme,
  focused: boolean,
  checked: boolean,
  disabled: boolean,
  styleFuncOrObject?: IStyleFunctionOrObject<
    ICheckboxStyleProps,
    ICheckboxStyles
  >
) => IStyleFunctionOrObject<ICheckboxStyleProps, ICheckboxStyles> = (
  theme: ITheme,
  focused: boolean,
  checked: boolean,
  disabled: boolean,
  styleFuncOrObject?: IStyleFunctionOrObject<
    ICheckboxStyleProps,
    ICheckboxStyles
  >
): IStyleFunctionOrObject<ICheckboxStyleProps, ICheckboxStyles> => {
  if (styleFuncOrObject === undefined) {
    return getCheckboxClassNames(theme, focused, checked, disabled);
  }

  if (typeof styleFuncOrObject === "function") {
    return (props: ICheckboxStyleProps) => {
      return mergeStyleSets(
        getCheckboxClassNames(theme, focused, checked, disabled),
        styleFuncOrObject(props)
      );
    };
  }

  return mergeStyleSets(
    getCheckboxClassNames(theme, focused, checked, disabled),
    styleFuncOrObject
  );
};
